import { useMutation } from '@apollo/client';
import React from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { MenuItem } from '../../components/Menu';
import Toast from '../../components/Toast';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { PostType } from '../../graphql/generated/graphql';
import templateIcon from '../../icons/non-prefilled/16x16/template.svg';
import templatePrefilledIcon from '../../icons/prefilled/16x16/template.svg';
import { UIActions } from '../../ui/uiSlice';

const TEMPLATIZE_MENU_ITEM_POST_FRAGMENT = gql(`
  fragment TemplatizeMenuItem_post on Post {
    id
    type
  }
`);

const UPDATE_POST_TYPE_MUTATION = gql(/** @complexity 2 */ `
  mutation updatePostType($postId: ID!, $type: PostType!) {
    updatePost(id: $postId, type: $type) {
      id
      ...TemplatizeMenuItem_post
    }
  }
`);

interface TemplatizeMenuItemProps {
  post: DocumentType<typeof TEMPLATIZE_MENU_ITEM_POST_FRAGMENT>;
}

const TemplatizeMenuItem = ({ post }: TemplatizeMenuItemProps) => {
  const dispatch = useAppDispatch();

  const [updatePostType] = useMutation(UPDATE_POST_TYPE_MUTATION);

  const handleClick = async () => {
    if (post.type === PostType.Post) {
      const { data } = await updatePostType({
        variables: { postId: post.id, type: PostType.Template },
      });
      if (data) {
        dispatch(
          UIActions.pushToast((toastProps) => (
            <Toast
              title="Converted into template"
              message="You can now use this template to create brand new posts as a copy of this template’s contents."
              primaryText="Got it!"
              onPrimaryClick={() => {
                toastProps.onDismiss();
              }}
              secondaryText="Undo"
              onSecondaryClick={() => {
                updatePostType({
                  variables: { postId: post.id, type: PostType.Post },
                });
                toastProps.onDismiss();
              }}
              onDismiss={toastProps.onDismiss}
              icon={templatePrefilledIcon}
            />
          )),
        );
      }
    } else {
      await updatePostType({
        variables: { postId: post.id, type: PostType.Post },
      });
    }
  };

  return (
    <MenuItem icon={templateIcon} onClick={handleClick}>
      {post.type === PostType.Template ? 'Convert to post' : 'Templatize'}
    </MenuItem>
  );
};

export default TemplatizeMenuItem;
